import { useState, useEffect } from 'react'
import Loading from '@/components/Loading'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { routes } from '@/routes/utils'
import { useTranslation } from "react-i18next"
import { clearAllCookies } from '@/utils/auth'

export function AzureLoginCallback() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery(
    ['azure-callback', code],
    async () => {
      try {
        // Make sure axios is configured to send cookies
        const response = await axios.get(
          `${import.meta.env.VITE_BASE_URL}/api/auth/azure-callback/?code=${code}`,
          {
            withCredentials: true, // This is critical for session cookies
          },
        )
        return response.data
      } catch (err: any) {
        console.log('Error during Azure login callback:', err)
        setError(err?.response?.data?.error || 'An error occurred during authentication')

        throw err
      }
    },
    {
      enabled: !!code,
      retry: false,
    },
  )

  useEffect(() => {
    if (data) {
      localStorage.setItem('org-type', 'company')
      localStorage.removeItem('failed-login-with-microsoft')
      // Redirect to home or dashboard
      navigate('/')
    }
  }, [data, navigate])

  useEffect(() => {
    setTimeout(() => {
      if (!code) {
        navigate(routes.requestCode.getPath())
      }
    }, 1000)
  }, [code, navigate])

  // Function to clear cookies and redirect
  const handleBackToLogin = () => {
    localStorage.setItem('failed-login-with-microsoft', 'true')
    
    // Clear all browser cookies to ensure clean login state
    clearAllCookies()
    
    // Redirect to login page
    navigate(routes.requestCode.getPath())
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (isError || error) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center">
        <div className="text-red-500">{error ? t(error) : 'An error occurred during authentication'}</div>
        <button
          className="mt-4 rounded bg-blue-500 px-4 py-2 text-white"
          onClick={handleBackToLogin}
        >
          {t("button.back_to_login")}
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Loading />
    </div>
  )
}
